import React, { useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Thermal ticket printout (58mm). Designed for AirPrint on iPad with thermal printers
 * (Star, Epson, Bixolon, etc.). Uses @media print CSS to constrain layout to receipt width.
 *
 * Props:
 *   sale: { id, total_amount, payment_method, delivery_method, created_at, customer, items, seller_name }
 *   company: { legal_name, rfc, address, phone, website }
 *   appUrl: base URL used to build /cert/<folio> QR
 *   autoPrint: when true, triggers window.print() after mount
 */
const PAY_LABEL = { cash: 'Efectivo', credit: 'T. Crédito', debit: 'T. Débito', transfer: 'Transferencia', card: 'Tarjeta' };

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n || 0);

const TicketReceipt = ({ sale, company = {}, appUrl = '', autoPrint = false }) => {
    const printed = useRef(false);

    useEffect(() => {
        if (autoPrint && sale && !printed.current) {
            printed.current = true;
            // small delay so QR rendering is complete
            const t = setTimeout(() => window.print(), 350);
            return () => clearTimeout(t);
        }
    }, [autoPrint, sale]);

    if (!sale) return null;

    const date = sale.created_at ? new Date(sale.created_at) : new Date();
    const dateStr = date.toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City', day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('es-MX', { timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit' });

    const items = sale.items || [];

    return (
        <>
            {/* Print-only stylesheet — 58mm thermal paper */}
            <style>{`
                @page { size: 58mm auto; margin: 2mm; }
                @media print {
                    html, body { background: #fff !important; }
                    body * { visibility: hidden !important; }
                    .ticket-print, .ticket-print * { visibility: visible !important; }
                    .ticket-print {
                        position: absolute !important;
                        left: 0; top: 0;
                        width: 58mm !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        background: #fff !important;
                        color: #000 !important;
                        font-family: 'Courier New', 'Menlo', monospace !important;
                        font-size: 10pt !important;
                        line-height: 1.25 !important;
                    }
                    .ticket-print img { max-width: 100% !important; }
                    .no-print { display: none !important; }
                }
                .ticket-print {
                    width: 58mm;
                    max-width: 100%;
                    margin: 0 auto;
                    padding: 12px;
                    background: #FFFFFF;
                    color: #000;
                    font-family: 'Courier New', 'Menlo', monospace;
                    font-size: 11pt;
                    line-height: 1.3;
                }
                .ticket-print .center { text-align: center; }
                .ticket-print .right { text-align: right; }
                .ticket-print .bold { font-weight: 700; }
                .ticket-print .big { font-size: 13pt; font-weight: 700; }
                .ticket-print .xl { font-size: 16pt; font-weight: 700; }
                .ticket-print .sm { font-size: 9pt; }
                .ticket-print .dashed {
                    border-top: 1px dashed #000;
                    margin: 6px 0;
                }
                .ticket-print .row {
                    display: flex;
                    justify-content: space-between;
                    gap: 8px;
                }
                .ticket-print .item {
                    margin-bottom: 4px;
                }
                .ticket-print .item-line {
                    display: flex;
                    justify-content: space-between;
                    gap: 6px;
                }
                .ticket-print .item-name {
                    flex: 1;
                    word-break: break-word;
                }
                .ticket-print .folio {
                    font-family: monospace;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                }
            `}</style>

            <div className="ticket-print" data-testid="ticket-print">
                {/* Header */}
                <div className="center">
                    <div className="xl">{company.legal_name || 'Exploremos'}</div>
                    {company.rfc && <div className="sm">RFC: {company.rfc}</div>}
                    {company.address && <div className="sm">{company.address}</div>}
                    {(company.phone || company.email) && (
                        <div className="sm">
                            {[company.phone, company.email].filter(Boolean).join(' · ')}
                        </div>
                    )}
                </div>

                <div className="dashed" />

                {/* Receipt meta */}
                <div className="center bold">RECIBO DE VENTA</div>
                <div className="row sm" style={{ marginTop: 4 }}>
                    <span>Fecha:</span>
                    <span>{dateStr} {timeStr}</span>
                </div>
                {sale.seller_name && (
                    <div className="row sm">
                        <span>Atendió:</span>
                        <span>{sale.seller_name}</span>
                    </div>
                )}
                {sale.customer?.name && (
                    <div className="row sm">
                        <span>Cliente:</span>
                        <span className="right" style={{ maxWidth: '60%' }}>{sale.customer.name}</span>
                    </div>
                )}

                <div className="dashed" />

                {/* Items */}
                <div className="bold sm" style={{ marginBottom: 4 }}>CERTIFICADOS</div>
                {items.map((it, idx) => {
                    const folio = it.folio || it.code || '';
                    const certUrl = folio && appUrl ? `${appUrl.replace(/\/$/, '')}/cert/${folio}` : '';
                    return (
                        <div key={idx} className="item">
                            <div className="item-line">
                                <span className="item-name bold">{it.product_name || it.name || 'Certificado'}</span>
                                <span className="right">{fmt(it.unit_price || it.price || 0)}</span>
                            </div>
                            <div className="sm folio">{folio}</div>
                            {certUrl && (
                                <div className="center" style={{ margin: '6px 0' }}>
                                    <QRCodeSVG value={certUrl} size={120} level="M" />
                                    <div className="sm" style={{ marginTop: 2, wordBreak: 'break-all' }}>
                                        {certUrl.replace(/^https?:\/\//, '')}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className="dashed" />

                {/* Totals */}
                <div className="row big">
                    <span>TOTAL</span>
                    <span>{fmt(sale.total_amount)}</span>
                </div>
                <div className="row sm">
                    <span>Forma de pago:</span>
                    <span className="bold">{PAY_LABEL[sale.payment_method] || sale.payment_method || 'Efectivo'}</span>
                </div>
                {sale.delivery_method && (
                    <div className="row sm">
                        <span>Entrega:</span>
                        <span>{sale.delivery_method}</span>
                    </div>
                )}

                <div className="dashed" />

                {/* Footer */}
                <div className="center sm" style={{ marginTop: 6 }}>
                    ¡Gracias por tu compra!
                </div>
                <div className="center sm">
                    Escanea el QR para ver tu certificado
                </div>
                {company.website && (
                    <div className="center sm bold" style={{ marginTop: 4 }}>
                        {company.website}
                    </div>
                )}
                <div className="center sm" style={{ marginTop: 8, fontSize: 8 }}>
                    Folio interno: {(sale.id || '').slice(0, 8).toUpperCase()}
                </div>
            </div>
        </>
    );
};

export default TicketReceipt;
